import React, { useState, useEffect, Fragment } from "react";
import { db } from "../../firebase/firebase-config";
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, query, where } from "firebase/firestore";
import { useParams } from "react-router-dom";
import AssessmentHistory from "./AssessmentHistory";
import AddAssessmentModal from "./AddAssessmentModal";
import { Dialog, Transition } from "@headlessui/react";

interface Patient {
    id: string;
    name: string;
    email: string;
    additionalInfo?: { key: string; value: string }[];
    painDetails?: { P: string; Q: string; R: string; S: string; T: string };
}

interface Assessment {
    id: string;
    date: string;
    time: string;
    painLevel: string;
    nonPharmacologicalIntervention: string;
    syariahIntervention: string;
    tanggalKajiUlang: string;
}

const PatientDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [patient, setPatient] = useState<Patient | null>(null);
    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [modal2, setModal2] = useState(false);
    const [editPainModal, setEditPainModal] = useState(false);
    const [painDetails, setPainDetails] = useState<{ P: string; Q: string; R: string; S: string; T: string }>({ P: "", Q: "", R: "", S: "", T: "" });

    useEffect(() => {
        const fetchPatient = async () => {
            if (id) {
                const patientDoc = await getDoc(doc(db, "patients", id));
                if (patientDoc.exists()) {
                    const data = patientDoc.data();
                    setPatient({
                        id: patientDoc.id,
                        ...data,
                        additionalInfo: Array.isArray(data.additionalInfo) ? data.additionalInfo : [],
                        painDetails: data.painDetails || { P: "", Q: "", R: "", S: "", T: "" }
                    } as Patient);
                    setPainDetails(data.painDetails || { P: "", Q: "", R: "", S: "", T: "" });
                }
            }
        };

        const fetchAssessments = async () => {
            if (id) {
                const q = query(collection(db, "intervention_records"), where("patientId", "==", id));
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })) as Assessment[];
                setAssessments(data);
            }
        };

        fetchPatient();
        fetchAssessments();
    }, [id]);

    const handleSavePainDetails = async () => {
        if (patient) {
            const patientRef = doc(db, "patients", patient.id);
            await updateDoc(patientRef, { painDetails });
            setPatient({ ...patient, painDetails });
            setEditPainModal(false);
        }
    };
    const handleNewAssessment = async (newAssessment: Assessment) => {
        try {
            await addDoc(collection(db, "intervention_records"), newAssessment);
            setAssessments((prev) => [...prev, newAssessment]);
            setModal2(false);
        } catch (error) {
            console.error("Error saving data: ", error);
        }
    };

    return (
        <div className="mx-auto p-6">
            {patient ? (
                <div>
                    <h2 className="text-2xl font-bold mb-2">Patient Detail</h2>
                    <p><strong>Name:</strong> {patient.name}</p>
                    <p><strong>Email:</strong> {patient.email}</p>
                    <hr className="my-4" />

                    <h3 className="text-xl font-semibold mb-2">Informasi Nyeri</h3>
                    <div className="flex justify-between items-center mb-4 mt-20">
                        <h3 className="text-xl font-semibold">Informasi Nyeri</h3>
                        <button
                            className="bg-blue-500 text-white px-4 py-2 rounded"
                            onClick={() => setEditPainModal(true)}
                        >
                            Edit Nyeri
                        </button>
                    </div>
                    <table className="w-full border border-gray-300">
                        <tbody className="divide-y divide-gray-200">
                        {Object.entries(patient.painDetails || {}).map(([key, value]) => (
                            <tr key={key} className="border">
                                <td className="w-[80px] px-4 py-2 font-bold bg-gray-100 border text-left">{key}</td>
                                <td className="px-4 py-2 border text-left">{value}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/*<button className="bg-blue-500 text-white px-4 py-2 rounded mt-2" onClick={() => setEditPainModal(true)}>*/}
                    {/*    Edit Nyeri*/}
                    {/*</button>*/}


                    <Transition appear show={editPainModal} as={Fragment}>
                        <Dialog as="div" className="fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50" onClose={() => setEditPainModal(false)}>
                            <Transition.Child enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                                    <Dialog.Title className="text-lg font-bold mb-4">Edit Informasi Nyeri</Dialog.Title>

                                    <div className="space-y-4">
                                        {Object.entries(painDetails).map(([key, value]) => (
                                            <div key={key}>
                                                <label className="block font-semibold">{key}</label>
                                                <input className="border p-2 w-full rounded" value={value} onChange={(e) => setPainDetails({ ...painDetails, [key]: e.target.value })} />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 flex justify-end space-x-2">
                                        <button className="bg-gray-300 px-4 py-2 rounded" onClick={() => setEditPainModal(false)}>Cancel</button>
                                        <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleSavePainDetails}>Save</button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </Dialog>
                    </Transition>


                    <div className="flex justify-between items-center mb-4 mt-20">
                        <h3 className="text-xl font-semibold">Pain Assessment</h3>
                        <button
                            className="bg-blue-500 text-white px-4 py-2 rounded"
                            onClick={() => setModal2(true)}
                        >
                            Add Assessment
                        </button>
                    </div>

                    <hr className="my-4" />
                    <h3 className="text-xl font-semibold mb-2">Assessment History</h3>
                    <AssessmentHistory assessments={assessments} />
                    <AddAssessmentModal
                        isOpen={modal2}
                        onClose={() => setModal2(false)}
                        onSubmit={handleNewAssessment}
                        patientId={id}
                    />
                </div>

            ) : (
                <p>Loading patient details...</p>
            )}
        </div>
    );
};

export default PatientDetail;
